import { useEffect, useMemo, useRef, useState } from "react";
import { usePlannerLens } from "@/hooks/usePlannerLens";
import { readyBlockedCounts } from "@shared/executor/task-counts";
import { prunedCounts, useNoneAnswers } from "@/components/interview/useNoneAnswer";

/** How long the sparkle or the check stays beside the number — "about three seconds" (rule 127(2)). */
export const CHEER_MS = 3000;

export type Cheer = "gone" | "ready" | null;

/**
 * PLAN-HOME-1 (s228) — the executor-task counter on the Executor tab (rule 127(2)): tasks | ready, from the ONE
 * shared function (`readyBlockedCounts`, D-156), less the declared prunes of any picked "none" answer — exactly the
 * numbers the interview shell showed. A task going away shows a sparkle beside the tasks number, a task becoming
 * ready shows a check beside the ready number, each for about three seconds, no words (rule 53).
 *
 * The first observation is the baseline and shows nothing: arriving on a page is not a change the planner made.
 */
export function useTaskCounter(clientId: string) {
  const { data: lens } = usePlannerLens(clientId);
  const counts = useMemo(
    () => readyBlockedCounts(lens?.taskKeyUniverse ?? [], lens?.gaps ?? []),
    [lens?.taskKeyUniverse, lens?.gaps],
  );
  const none = useNoneAnswers();
  const shown = prunedCounts(counts, none.prune);

  const prev = useRef<{ total: number; ready: number } | null>(null);
  // `n` restarts the timer when the same cheer fires twice in a row.
  const [cheer, setCheer] = useState<{ kind: Cheer; n: number }>({ kind: null, n: 0 });
  useEffect(() => {
    if (!lens) return;
    const p = prev.current;
    prev.current = { total: shown.total, ready: shown.ready };
    if (!p) return;
    const kind: Cheer = shown.total < p.total ? "gone" : shown.ready > p.ready ? "ready" : null;
    if (kind) setCheer((c) => ({ kind, n: c.n + 1 }));
  }, [lens, shown.total, shown.ready]);
  useEffect(() => {
    if (!cheer.kind) return;
    const t = window.setTimeout(() => setCheer((c) => ({ kind: null, n: c.n })), CHEER_MS);
    return () => window.clearTimeout(t);
  }, [cheer.kind, cheer.n]);

  return { loaded: !!lens, total: shown.total, ready: shown.ready, cheer: cheer.kind, lens };
}
